// Seeded 2D Perlin (gradient) noise, plus fractal Brownian motion
// over it.
//
// Same requirement as the rest of world-gen: a PURE function of the
// seed, so the server and every client sample identical terrain with
// nothing to store or send. The permutation table is built from the
// seed, so a different seed is a different world and the same seed is
// always the same one.
//
// Perlin rather than plain value noise because we want smooth,
// organic streaks — value noise's grid artefacts would show up as a
// visible lattice in the terrain, which is the opposite of what
// "flowing horizontal stretches" wants.

// A seeded [0,1) stream — the same mulberry32 the chunks use, kept
// local so this module stands alone.
function mulberry(a) {
    return function () {
        a |= 0; a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// The classic Perlin permutation: 0..255 shuffled, then doubled so
// lookups never wrap-check. Seeded, so the whole noise field is
// determined by `seed`.
function buildPerm(seed) {
    const perm = Array.from({ length: 256 }, (_, i) => i);
    const rnd = mulberry(seed >>> 0);
    for (let i = 255; i > 0; i--) {
        const j = Math.floor(rnd() * (i + 1));
        [perm[i], perm[j]] = [perm[j], perm[i]];
    }
    const p = new Uint8Array(512);
    for (let i = 0; i < 512; i++) p[i] = perm[i & 255];
    return p;
}

const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10); // Perlin's 6t^5-15t^4+10t^3
const lerp = (a, b, t) => a + (b - a) * t;

// Gradient: dot the corner's pseudo-random direction with the
// distance vector. Eight directions is plenty in 2D and avoids the
// axis-aligned clumping the original four had.
function grad(h, x, y) {
    switch (h & 7) {
        case 0: return x + y;
        case 1: return -x + y;
        case 2: return x - y;
        case 3: return -x - y;
        case 4: return x;
        case 5: return -x;
        case 6: return y;
        default: return -y;
    }
}

// One octave of Perlin, in roughly [-1, 1].
function perlin2(p, x, y) {
    const X = Math.floor(x) & 255, Y = Math.floor(y) & 255;
    const xf = x - Math.floor(x), yf = y - Math.floor(y);
    const u = fade(xf), v = fade(yf);
    const aa = p[p[X] + Y], ab = p[p[X] + Y + 1];
    const ba = p[p[X + 1] + Y], bb = p[p[X + 1] + Y + 1];
    const x1 = lerp(grad(aa, xf, yf), grad(ba, xf - 1, yf), u);
    const x2 = lerp(grad(ab, xf, yf - 1), grad(bb, xf - 1, yf - 1), u);
    return lerp(x1, x2, v);
}

/**
 * Build a noise sampler for a seed. Returns { noise2, fbm }.
 *
 * `fbm` stacks octaves — each one higher frequency and lower amplitude — so the
 * terrain has both broad shapes and fine edges instead of one smooth blur.
 * Normalised back into ~[-1, 1] so a threshold means the same thing at any octave
 * count.
 */
export function makeNoise(seed) {
    const p = buildPerm(seed);
    const noise2 = (x, y) => perlin2(p, x, y);

    function fbm(x, y, octaves = 2, gain = 0.5, lacunarity = 2) {
        let sum = 0, amp = 1, freq = 1, norm = 0;
        for (let o = 0; o < octaves; o++) {
            sum += amp * perlin2(p, x * freq, y * freq);
            norm += amp;
            amp *= gain;
            freq *= lacunarity;
        }
        return sum / norm;
    }

    return { noise2, fbm };
}
